import { useCallback, useRef, useState } from "react";
import { SorterResult } from "antd/lib/table/interface";
import { useHistory } from "react-router";
import { RouteConfigComponentProps } from "react-router-config";
import { Button, DatePicker, Input, InputNumber } from "antd";

import ProTable, { ActionType } from "@ant-design/pro-table";
import { ExportableColumn } from "../../shared/Exporter";
import GraphqlService from "../../services/graphql/GraphqlService";
import { ABM, MomentJS, Tools } from "../../shared";
import CustomFilter from "../CustomFilter/CustomFilter";
import ButtonLiderar from "../ButtonLiderar/ButtonLiderar";
import { IAuditLog } from "../../services/IAuditLog";
import { getPaginationArgs, policyFormat } from "../../shared/utils";
import {
  FORMAT_DATE_TIME_1,
  FORMAT_DATE_TIME_2,
  FORMAT_DATE_TIME_4,
} from "../../shared/MomentJS";
import { useCheckAuthority } from "../../shared/CustomHooks";
import { ParamsType } from "@ant-design/pro-provider";

import "./style.less";
import { FileOutlined } from "@ant-design/icons";
import { IBaseFile } from "../../services/defaultSchema";

/**
 * Configure manualmente los campos de filtrado
 */
const TITLE_TABLE = "Auditoría";
const LIST_FILTER = [
  "id",
  "id_policy",
  "license_plate",
  "sent_to",
  "its_range",
];
const LIST_SORTER = [
  "its",
  "id",
];

export default function AuditLogList(
  routeProps: RouteConfigComponentProps
): JSX.Element {
  // // props

  // // states
  const [sorter, setSorter] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const [downloading, setDownloading] = useState<boolean>(false);

  // // services and hooks
  const { Query, customRequest } = GraphqlService();
  const history = useHistory();
  // // refs
  const actionRef = useRef<ActionType>();
  const variables = useRef<any>({});

  // // methods

  const request = async (
    params: ParamsType & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    }
  ) => {
    try {
      delete variables.current.filter;
      delete variables.current.orderBy;
      variables.current = {};
      const search: any = ABM.valuesResult(params);

      LIST_FILTER.forEach((element) => {
        try {
          if (search[element]) {
            if (!variables.current.filter) {
              variables.current.filter = {};
            }
            variables.current.filter[element] = search[element];
          }
        } catch (error) {
          // este error esta contemplado porque seguro el filtro que busca no se encuentra
        }
      });

      LIST_SORTER.forEach((element) => {
        try {
          if (search._sorter[element]) {
            if (!variables.current.orderBy) {
              variables.current.orderBy = {};
            }
            variables.current.orderBy.direction =
              Tools.getTypeOrderByTableSortParam(search._sorter[element]);
            variables.current.orderBy.field = element;
          }
        } catch (error) {
          // este error esta contemplado porque seguro el filtro que busca no se encuentra
        }
      });

      variables.current.filter = {
        ...variables.current.filter,
      };

      const { skip, take } = getPaginationArgs(
        params.pageSize || 20,
        params.current
      );

      variables.current.skip = skip;
      variables.current.take = take;

      const countPromise = customRequest({
        query: Query.auditLogCount,
        variables: variables.current,
      }).then((data: { count: number }) => data.count);

      const dataPromise = customRequest({
        query: Query.auditLogs,
        variables: variables.current,
      });

      const [totalItems, data] = await Promise.all([countPromise, dataPromise]);
      setTotal(totalItems);

      return {
        current: params.current,
        data,
        pageSize: params.pageSize,
        success: true,
        total: totalItems,
      };
    } catch (error) {
      return {
        current: params.current,
        data: [],
        pageSize: params.pageSize,
        success: false,
        total: 0,
      };
    }
  };
  
  const requestFile = async () => {
    setDownloading(true);
    try {
      const params = JSON.parse(JSON.stringify(variables.current));

      const result: IBaseFile = await customRequest({
        query: Query.auditLogFile,
        variables: {
          filter: params.filter,
          orderBy: params.orderBy,
        },
      });
      if (!result.file) {
        throw new Error("Ocurrió un error al descargar el archivo");
      }

      Tools.downloadFile({
        data: result.file as string,
        mimetype: result.mimetype,
        filename: result.filename,
        encoding: result.encoding,
      })
    } catch (error) {
      throw new Error("Ocurrió un error al descargar el archivo");
    }
    setDownloading(false);
  };

  const columns = useCallback(
    (): ExportableColumn<IAuditLog>[] => [
      {
        export: false,
        dataIndex: "id",
        title: "ID",
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
        sorter: true,
        type: ABM.TYPE_COLUMN.NUMBER,
        render: (_: any, record: IAuditLog) =>
          record?.id || "-",
         renderFormItem: () => <InputNumber placeholder="ID" min={0} />,
      },
      {
        export: false,
        dataIndex: "action",
        title: "Acción",
        sorter: false,
        align: "left",
        hideInTable: false,
        hideInSearch: true,
        hideInForm: true,
        type: ABM.TYPE_COLUMN.STRING,
        render: (_: any, record: IAuditLog) =>
          record.audit_log_action.name || "-",
      },
      {
        export: false,
        dataIndex: "its",
        title: "Fecha",
        type: ABM.TYPE_COLUMN.DATE,
        render: (_: any, record: IAuditLog) => (
          <p className="non-wrap-cell">
            {record.its
              ? MomentJS.specificMoment(
                  {
                    date: record.its,
                    format: FORMAT_DATE_TIME_2,
                    utc: true,
                  },
                  FORMAT_DATE_TIME_1,
                  true
                )
              : "-"}
          </p>
        ),
        align: "left",
        sorter: true,
        defaultSortOrder: "descend",
        hideInTable: false,
        hideInSearch: true,
        hideInForm: true,
      },
      {
        export: false,
        dataIndex: "its_range",
        title: "Fecha",
        type: ABM.TYPE_COLUMN.DATE,
        renderFormItem: () => (
          <DatePicker.RangePicker
            name="its_range"
            format={FORMAT_DATE_TIME_4}
            placeholder={["Fecha desde", "Fecha hasta"]}
            allowEmpty={[true, true]}
            className="renderFormItem"
            allowClear
          />
        ),
        hideInTable: true,
        hideInSearch: false,
        hideInForm: true,
      },
      {
        export: false,
        dataIndex: "username",
        title: "Usuario",
        sorter: false,
        align: "left",
        hideInTable: false,
        hideInSearch: true,
        hideInForm: true,
        type: ABM.TYPE_COLUMN.STRING,
        render: (_: any, record: IAuditLog) =>
          record.user.username || "-",
      },
      {
        export: false,
        dataIndex: "id_policy",
        title: "Póliza",
        align: "left",
        sorter: true,
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
        type: ABM.TYPE_COLUMN.STRING,
        render: (_: any, record) => record.id_policy ? policyFormat(record.id_policy) : "-",
        renderFormItem: () => <Input placeholder="Póliza" />,
      },
      {
        export: false,
        dataIndex: "license_plate",
        title: "Patente",
        type: ABM.TYPE_COLUMN.STRING,
        render: (_: any, record: IAuditLog) =>
          record?.license_plate || "-",
        align: "left",
        sorter: false,
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
        renderFormItem: () => <Input placeholder="Patente" />,
      },
      {
        export: false,
        dataIndex: "sent_to",
        title: "Enviado a",
        type: ABM.TYPE_COLUMN.STRING,
        render: (_: any, record: IAuditLog) =>
          record?.sent_to || "-",
        align: "left",
        sorter: false,
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
        renderFormItem: () => <Input placeholder="Correo electrónico" />,
      },
    ],
    []
  );

  return (
    useCheckAuthority(routeProps) || (
      <>
        <h1>{TITLE_TABLE}</h1>
        <CustomFilter header="Filtrar por" />
        <ProTable<IAuditLog>
          defaultSize="small"
          actionRef={actionRef}
          rowKey="id"
          search={{
            collapsed: false,
            resetText: "Limpiar búsqueda",
            searchText: "Buscar",
            collapseRender: false,
          }}
          onChange={(_, _filter, _sorter) => {
            const sorterResult = _sorter as SorterResult<IAuditLog>;
            if (sorterResult.field) {
              setSorter(`${sorterResult.field}_${sorterResult.order}`);
            }
          }}
          params={{
            sorter,
          }}
          className="audit-log-table"
          request={async (_params, _sorter, _filter) =>
            request({ ..._params, _sorter, _filter })
          }
          columns={columns()}
          scroll={{ x: "scroll" }}
          toolBarRender={() => [
            <>
              <Button
                type="primary"
                icon={<FileOutlined />}
                style={{
                  width: "auto",
                }}
                loading={downloading}
                disabled={!total}
                onClick={() => {
                  requestFile();
                }}
              >
                Descargar Excel
              </Button>
            </>,
          ]}
        />
        <div className="buttons">
          <ButtonLiderar
            color="white"
            buttonName="Volver"
            className="button-left"
            onClick={() => history.goBack()}
          />
        </div>
      </>
    )
  );
}
